import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Paper,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { getAccountById, updateAccount, updateAccountPermissions } from "../../../api/account";
import { getResources } from "../../../api/app";
import { getAccountUsers } from "../../../api/users";
import AccountInfo from "../../../components/Accounts/AccountInfo";
import PermissionsForm from "../../../components/Accounts/PermissionsForm";
import UserTable from "../../../components/Users/UserTable";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { RootState } from "../../../store";
import { setLoading, setTab } from "../../../store/UI";
import { Account, ResourcePermission, UpdateAccountRequest } from "../../../types/model/account";
import { Resource } from "../../../types/model/app/PrivateSettings";
import { User } from "../../../types/model/user";
import { StoreInterfaces, UIStore } from "../../../types/store";
import { UserResponse } from "../../../types/model/user/UserResponse";

export default function DetailsAccount() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const location = useLocation();

  const { accountId } = useParams<{ accountId: string }>();

  const tabState = useSelector((state: { ui: UIStore }) => state.ui.tabState);
  const [tabIndex, setTabIndex] = useState(tabState[location.pathname] || 0);
  const [accountData, setAccountData] = useState<Account>();
  const [usersData, setUsersData] = useState<UserResponse[]>([]);

  const loading = useSelector((state: RootState) => state.ui.loading);
  const session = useSelector((state: StoreInterfaces) => state.session);

  const [resources, setResources] = useState<Resource[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());
  const [savedKeys, setSavedKeys] = useState<Set<string>>(new Set());
  const [isEditingPermissions, setIsEditingPermissions] = useState(false);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);

  const isModule = (key: string) => key.startsWith("MODULE_");
  const childrenOf = (parentKey: string) =>
    resources.filter((r) => r.parentKey === parentKey && r.active);

  const fetchResources = async (currentPermissions: string[]) => {
    setResourcesLoading(true);
    try {
      const data = await getResources();
      setResources(data);

      if (currentPermissions.length === 0) {
        const allActiveKeys = new Set(data.filter((r) => r.active).map((r) => r.key));
        setCheckedKeys(allActiveKeys);
        setSavedKeys(allActiveKeys);
      } else {
        setCheckedKeys(new Set(currentPermissions));
        setSavedKeys(new Set(currentPermissions));
      }
      setResourcesLoaded(true);
    } catch (error) {
      handleError(error);
    } finally {
      setResourcesLoading(false);
    }
  };

  const handleTogglePermission = (key: string, isParent: boolean) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      const children = childrenOf(key).map((c) => c.key);

      if (next.has(key)) {
        next.delete(key);
        if (isParent) children.forEach((c) => next.delete(c));
      } else {
        next.add(key);
        if (isParent) children.forEach((c) => next.add(c));
      }

      if (!isParent) {
        const parent = resources.find((r) =>
          childrenOf(r.key).some((c) => c.key === key)
        );
        if (parent) {
          const allSiblings = childrenOf(parent.key).map((c) => c.key);
          if (allSiblings.every((s) => !next.has(s))) next.delete(parent.key);
          else next.add(parent.key);
        }
      }

      return next;
    });
  };

  const buildPermissionsPayload = (): ResourcePermission[] =>
    resources
      .filter((r) => r.active)
      .map((r) => ({
        resourceKey: r.key,
        active: isModule(r.key) ? true : checkedKeys.has(r.key),
      }));

  const handleSavePermissions = async () => {
    if (!accountId) return;
    setIsSavingPermissions(true);
    try {
      await updateAccountPermissions(Number.parseInt(accountId), buildPermissionsPayload());
      setSavedKeys(new Set(checkedKeys));
      setIsEditingPermissions(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSavingPermissions(false);
    }
  };

  const handleCancelPermissions = () => {
    setCheckedKeys(new Set(savedKeys));
    setIsEditingPermissions(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    dispatch(setTab({ page: location.pathname, tab: newValue }));
  };

  const handleAccountUpdate = async (values: UpdateAccountRequest) => {
    try {
      await updateAccount(Number.parseInt(accountId!), values);
      const data = await getAccountById(Number.parseInt(accountId!));
      setAccountData(data);
    } catch (error) {
      handleError(error);
    }
  };

  const handleUsersTableRequestRefresh = () => {
    if (!accountId) return;
    dispatch(setLoading(true));
    getAccountUsers(Number.parseInt(accountId))
      .then((data) => {
        setUsersData(data);
      })
      .catch((error) => handleError(error))
      .finally(() => dispatch(setLoading(false)));
  };

  const handleUserRowClick = (user: User) => {
    if (session.isAdministrator) {
      navigate(`/admin/users/${user.accountId}/${user.id}`);
    }
  };

  useEffect(() => {
    if (accountId) {
      (async () => {
        dispatch(setLoading(true));
        try {
          const data = await getAccountById(Number.parseInt(accountId));
          setAccountData(data);
        } catch (error) {
          handleError(error);
        } finally {
          dispatch(setLoading(false));
        }
      })();
    }
  }, [accountId]);

  useEffect(() => {
    if (tabIndex === 1 && accountId) {
      (async () => {
        dispatch(setLoading(true));
        try {
          const data = await getAccountUsers(Number.parseInt(accountId));
          setUsersData(data);
        } catch (error) {
          handleError(error);
        } finally {
          dispatch(setLoading(false));
        }
      })();
    }
  }, [tabIndex, accountId]);

  useEffect(() => {
    if (tabIndex === 2 && accountData && !resourcesLoaded) {
      fetchResources(accountData.permissions ?? []);
    }
  }, [tabIndex, accountData, resourcesLoaded]);

  return (
    <Paper elevation={3} sx={{ borderRadius: 2 }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100% " }} variant="h4">
          {t("admin.accounts.account.label")}
          {accountData ? ` - ${accountData.name}` : ""}
        </Typography>

        <Tooltip title={t("generic.back")}>
          <Button
            onClick={() => navigate(-1)}
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            <ArrowBackIcon />
          </Button>
        </Tooltip>
      </Toolbar>

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        <Tab label={t("admin.accounts.tabs.general")} />
        <Tab label={t("admin.accounts.tabs.users")} />
        <Tab label={t("admin.accounts.tabs.permissions")} />
      </Tabs>

      <Box sx={{ p: 3 }}>
        <Fade in={tabIndex === 0} timeout={300} unmountOnExit>
          <Box>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              tabIndex === 0 &&
              accountData && (
                <AccountInfo
                  accountData={accountData}
                  onSubmit={handleAccountUpdate}
                />
              )
            )}
          </Box>
        </Fade>

        <Fade in={tabIndex === 1} timeout={300} unmountOnExit>
          <Box>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              tabIndex === 1 &&
              usersData && (
                <UserTable
                  tableData={usersData}
                  accountId={Number.parseInt(accountId!)}
                  onRequestRefresh={handleUsersTableRequestRefresh}
                  onRowClick={handleUserRowClick}
                  showAddButton={true}
                />
              )
            )}
          </Box>
        </Fade>

        <Fade in={tabIndex === 2} timeout={300} unmountOnExit>
          <Box>
            {tabIndex === 2 && (
              <PermissionsForm
                resources={resources}
                loading={resourcesLoading}
                checkedKeys={checkedKeys}
                onToggle={handleTogglePermission}
                isEditing={isEditingPermissions}
                isSaving={isSavingPermissions}
                onEdit={() => setIsEditingPermissions(true)}
                onSave={handleSavePermissions}
                onCancel={handleCancelPermissions}
              />
            )}
          </Box>
        </Fade>
      </Box>
    </Paper>
  );
}